import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {catchError, exhaustMap, map, tap} from 'rxjs/operators';
import { LoadAppSettings, LoginActions } from "../actions";
import {Store} from '@ngrx/store';
import {AppState} from '../index';
import {of, take} from 'rxjs';
import {
  ActivationCodeReq, AppSettingReq,
  ContinueAsGuest,
  LoginReq,
  LoginSocialReq,
  RegisterReq,
  VerifyActivationCodeReq
} from "@core/models";
import {MultiLanguageService} from '@app/share/translate';
import {AccountService} from '@app/core/services/account/account.service';
import {FacebookPixelService} from "@share/pixel-code/facebook-pixel.service";
import {SnapPixelService} from "@share/pixel-code";
import { UtilService } from "@core/utils/util.service";

@Injectable()
export class LoginEffects {
  constructor(
    private _actions$: Actions,
    private _store$: Store<AppState>,
    private _router: Router,
    private _location: Location,
    private _multiLanguageService: MultiLanguageService,
    private accountService: AccountService,
    private _snackBar: UtilService,
    private readonly pixel: FacebookPixelService,
    private readonly snapPixelService: SnapPixelService
  ) {}

  // Login
  login$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.login),
        map((action) => action.payload),
        exhaustMap((login: LoginReq) => {
          return this.accountService.login(login).pipe(
            take(1),
            tap((payload) => {
              console.log(payload, payload?.Result?.StatusMessage);
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');

              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(LoginActions.loginSuccess({ payload }));
                if (payload.Customer?.UserType && !payload.Customer?.IsActive) {
                  this._router
                    .navigate(['/confirm'], {
                      queryParams: { next: 'home' },
                    })
                    .then();
                } else {
                  this._router.navigate(['/home']).then();
                }
              }
            }),
            catchError((payload) => {
              console.log(payload);
              this._store$.dispatch(LoginActions.loginError({ payload }));
              console.log('payload', payload?.Result?.StatusMessage);
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  ); // Social Login
  socialLogin$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.loginSocial),
        map((action) => action.payload),
        exhaustMap((login: LoginSocialReq) => {
          return this.accountService.loginSocial(login).pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this.snapPixelService.track('SIGN_UP', {
                  sign_up_method: 'Social',
                  success:1,
                  item_category: JSON.stringify(payload.Customer.UserType),
                });
                this._store$.dispatch(LoginActions.loginSuccess({ payload }));
                this._router.navigate(['/home']).then();
              }
            }),
            catchError((payload) => {
              this._store$.dispatch(LoginActions.loginError({ payload }));
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

// Guest
  continueAsGuest$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.loginGuest),
        map((action) => action.payload),
        exhaustMap((login: ContinueAsGuest) => {
          return this.accountService.continueAsGuest(login).pipe(
            take(1),
            tap((payload) => {
              if (payload.Customer.UserType !== 5) {
                this._snackBar.openSnackBar(
                  payload?.Result?.StatusMessage ?? ''
                );
              }
              this._store$.dispatch(LoginActions.loginSuccess({ payload }));
              this._router.navigate(['/']);
            }),
            catchError((payload) => {
              this._store$.dispatch(LoginActions.loginError({ payload }));
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  // Register
  register$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.register),
        map((action) => action.payload),
        exhaustMap((body: RegisterReq) => {
          return this.accountService.register(body).pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this.pixel.track('CompleteRegistration', {
                  status: payload.Customer.IsActive,
                  value: payload.Customer.UserType,
                });

                this._store$.dispatch(
                  LoginActions.registerSuccess({ payload })
                );
                //Stop Confirmation Otp Page For Registration
                this._router
                  .navigate(['/home'])
                  .then();
              } else {
                this._store$.dispatch(LoginActions.registerError({ payload }));
              }
            }),
            catchError((payload) => {
              this._store$.dispatch(LoginActions.registerError({ payload }));
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  // Verify Activation
  verifyCode$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.verificationCode),
        map((action) => action.payload),
        exhaustMap((body: VerifyActivationCodeReq) => {
          return this.accountService.verifyActivationCode(body).pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(
                  LoginActions.verificationCodeSuccess({ payload })
                );
              } else {
                this._store$.dispatch(
                  LoginActions.verificationCodeFail({ payload })
                );
              }
            }),
            catchError((payload) => {
              this._store$.dispatch(
                LoginActions.verificationCodeFail({ payload })
              );
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  // Resend Activation
  resendActivationCode$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.resendActivationCode),
        map((action) => action.payload),
        exhaustMap((body: ActivationCodeReq) => {
          return this.accountService.sendActivationCode(body).pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(
                  LoginActions.resendActivationCodeSuccess({ payload })
                );
              } else {
                this._store$.dispatch(
                  LoginActions.resendActivationCodeFail({ payload })
                );
              }
            }),
            catchError((payload) => {
              this._store$.dispatch(
                LoginActions.resendActivationCodeFail({ payload })
              );
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? 'error');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  // Forget Password
  forgetPassword$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.forgetPassword),
        map((action) => action.payload),
        exhaustMap((body) => {
          let req;
          if (body.useEmail) {
            req = this.accountService.forgotPasswordByEmail(body.key);
          } else {
            req = this.accountService.forgotPasswordByMobile(
              body.mobileCode ?? '',
              body.key
            );
          }
          return req.pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(
                  LoginActions.forgetPasswordSuccess({ payload })
                );
                this._router
                  .navigate(['/confirm'], {
                    queryParams: { next: 'reset' },
                  })
                  .then();
              } else {
                this._store$.dispatch(
                  LoginActions.forgetPasswordFail({ payload })
                );
              }
            }),
            catchError((payload) => {
              this._store$.dispatch(
                LoginActions.forgetPasswordFail({ payload })
              );
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  // Remove The Account
  removeAccount$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.removeAccount),
        exhaustMap(() => {
          return this.accountService.removeAccount().pipe(
            take(1),
            tap((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(
                  LoginActions.backToGuest({
                    payload: { redirectToLogin: true },
                  })
                );
              }
            }),
            catchError((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

  backToGuest$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(LoginActions.backToGuest),
        exhaustMap((backObject) => {
          this._store$.dispatch(
            LoadAppSettings({ payload: new AppSettingReq() })
          );
          return this.accountService.getCustomerProfile().pipe(
            take(1),
            tap((payload) => {
              // this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              if ([1, 2].includes(payload.Result.StatusCode)) {
                this._store$.dispatch(
                  LoginActions.StoreProfile({ payload: payload.Customer })
                );
              }
              if (backObject.payload.redirectToLogin) {
                this._router.navigate(['/login']).then();
              }
            }),
            catchError((payload) => {
              this._snackBar.openSnackBar(payload?.Result?.StatusMessage ?? '');
              return of(null);
            })
          );
        })
      ),
    { dispatch: false }
  );

 /* openSnackBar(msg: string) {
    this._snackBar.open(msg, 'Ok', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }*/
}
